<template>
  <!-- Sale-unit tiles: the photograph IS the card, with the text on a gradient scrim over
       its bottom third. Chosen from the __concepts/firesale-tile.html board (variant C3,
       Daniel 2026-08-30) over a hard photo/text seam and a dissolve fade. The tile carries
       the machine, one photo, and the two numbers every caller asks first - hours and
       asking price. No tag number, because a sale unit does not have one a customer would
       ever need to quote (see the note at the top of data/machinesForSale.js), and no
       owner, seller, or location.

       A tile with no photograph yet falls back to the card's own dark background - the
       layout holds, nothing collapses, and the listing is still reachable while the
       photo pass happens.

       Deliberately NOT MachineTiles.vue: that grid is built around a four-digit number as
       its headline, and bending it to have no number would leave both directories worse. -->
  <div class="listing-grid" :aria-label="ariaLabel">
    <router-link
      v-for="tile in tiles"
      :key="tile.slug"
      class="listing-card"
      :class="{ sold: tile.status === 'Sold' }"
      :to="`/forsale/${tile.slug}`"
    >
      <!-- alt is empty on purpose: the photo is the card's backdrop and the text beside it
           already says what the machine is - a screen reader should not hear it twice. -->
      <img v-if="tile.photo" class="tile-photo" :src="tile.photo" alt="" loading="lazy" decoding="async" />
      <span class="tile-scrim" aria-hidden="true"></span>

      <span class="tile-body">
        <span class="tile-kind">{{ tile.kind }}</span>
        <strong class="tile-title">{{ tile.title }}</strong>
        <span v-if="tile.status === 'Sale pending'" class="tile-pending">Sale pending</span>

        <span class="tile-facts">
          <span v-if="tile.reading" class="fact">
            <span class="fact-label">{{ tile.reading.unit === 'miles' ? 'Odometer' : 'Hours' }}</span>
            <strong>{{ tile.reading.value }}</strong>
          </span>
          <span class="fact">
            <span class="fact-label">Asking</span>
            <strong>{{ asking(tile) }}</strong>
          </span>
        </span>

        <span class="record-link">{{ linkText }} <span aria-hidden="true">→</span></span>
      </span>
    </router-link>
  </div>
</template>

<script>
export default {
  name: 'ListingTiles',
  props: {
    tiles: { type: Array, required: true },
    ariaLabel: { type: String, default: 'Machines for sale' },
    linkText: { type: String, default: 'See this machine' },
  },
  methods: {
    // "Contact us" rather than "Contact for price": the tile's fact slot is one short word
    // wide, and the listing page carries the full wording.
    asking(tile) {
      if (tile.status === 'Sold') return 'Sold';
      return tile.price || 'Contact us';
    },
  },
};
</script>

<style scoped>
.listing-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.listing-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  /* Tall enough that the photo survives the scrim with a clear upper two-thirds; the text
     block sets the real floor on short grids. */
  min-height: 340px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: var(--radius);
  background: linear-gradient(145deg, rgba(255, 90, 2, 0.065), transparent 48%);
  transition: border-color 0.3s ease, transform 0.35s ease;
}

.listing-card:hover,
.listing-card:focus-visible {
  border-color: var(--orange);
  transform: translateY(-2px);
}

.tile-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* The scrim is what buys the text its legibility on ANY photograph, so it is heavy at the
   floor and gone by the upper third. It sits over the photo and under the text. */
.tile-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0.78) 68%, rgba(0, 0, 0, 0.94) 100%);
}

/* A sold machine stays on the page (shared URLs must not 404) but stops selling itself:
   the photo goes grey and the card says so across its face. */
.sold .tile-photo {
  filter: grayscale(0.85) brightness(0.7);
}

.sold::after {
  content: 'SOLD';
  position: absolute;
  inset: 0 0 34% 0; /* centred in the photo's clear zone, above the scrim'd text block */
  display: grid;
  place-items: center;
  font-family: 'Jost', 'Barlow', sans-serif;
  font-size: 2.2rem;
  letter-spacing: 0.3em;
  color: var(--white);
}

.tile-body {
  position: relative; /* above the scrim */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: clamp(1rem, 2vw, 1.4rem);
}

.tile-kind {
  font-size: 0.68rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  opacity: 0.7;
}

.tile-title {
  margin-top: 0.4rem;
  color: var(--white);
  font-family: 'Jost', 'Barlow', sans-serif;
  font-size: clamp(1.5rem, 2.6vw, 2rem);
  font-weight: 400;
  line-height: 1.05;
}

.tile-pending {
  margin-top: 0.6rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid var(--orange);
  color: var(--orange);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.tile-facts {
  display: flex;
  gap: 1.4rem;
  margin: 0.6rem 0 0.9rem;
}

.fact {
  display: grid;
  gap: 0.1rem;
}

.fact-label {
  font-size: 0.65rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.6;
}

.fact strong {
  font-family: 'Jost', 'Barlow', sans-serif;
  font-size: 1.15rem;
  font-weight: 400;
}

.record-link {
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 0.55rem;
  border-top: 1px solid rgba(255, 90, 2, 0.55);
  color: var(--orange);
  font-weight: 500;
}

@media (max-width: 900px) {
  .listing-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .listing-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
